// Auth store — token, user info, api base, avatar.
//
// Migrado pra Firebase Auth (2026-05-19): o token agora e ID token JWT do
// Firebase, renovado automaticamente pelo SDK. Como Firebase armazena
// internamente o ID token em IndexedDB (browserLocalPersistence), nao
// precisamos persistir o token em localStorage — fonte de verdade e o
// Firebase Auth state.
//
// Pra retrocompat com codigo que ja existia chamando `get(token)`,
// mantemos um store reativo `token` que e atualizado por onIdTokenChanged
// — qualquer Bearer Authorization no resto do app continua usando $token
// (ou get(token)) sem alteracao.

import { writable, derived, get } from 'svelte/store';
import { DEFAULT_API_BASE, STORAGE_KEYS } from '../lib/constants';
import { firebaseAuth } from '../lib/firebase';
import { onIdTokenChanged } from 'firebase/auth';

export type UserProfile = {
  id: string;
  nickname: string;
  email?: string;
  avatar?: string;
  balance_cents?: number;
};

// Helper pra writable store sincronizada com localStorage.
function persistentString(key: string, initial: string) {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  const store = writable<string>(stored ?? initial);
  store.subscribe((v) => {
    if (typeof localStorage === 'undefined') return;
    if (v) localStorage.setItem(key, v);
    else localStorage.removeItem(key);
  });
  return store;
}

// Limpa o key legacy de apiBase do localStorage (campo "configurar
// backend" removido em 2026-05-29 — DEFAULT_API_BASE agora e a unica
// fonte). Sem isso fica lixo no localStorage do usuario.
if (typeof localStorage !== 'undefined') {
  try {
    localStorage.removeItem(STORAGE_KEYS.apiBase);
  } catch { /* ignore */ }
}

// Token agora vem do Firebase. Persistente em IndexedDB do Firebase.
// Mantido como writable pra Svelte reactivity — sub stores e api.ts
// continuam usando get(token) sem mudanca.
export const token = writable<string>('');

// userId / userName / avatar continuam em localStorage — perfil pode ser
// carregado antes do Firebase Auth completar restore, pra UI nao piscar.
// Mas a fonte de verdade do ID e o Firebase UID (= userId).
export const userId = persistentString(STORAGE_KEYS.userId, '');
export const userName = persistentString(STORAGE_KEYS.userName, '');
// userScore: score ELO do user logado, exibido no topbar (estilo mockup).
// Atualizado depois de cada GET /users/me + por refresh manual.
export const userScore = writable<number>(0);

// lastRankingDelta: ultimo delta de ranking aplicado (+10 / -10) numa
// partida de torneio. Setado pelo handler ranking:update (lobby.ts).
// FinishedModal le pra mostrar "+10 RANKING" no fim. `seq` incrementa
// a cada update pra forcar re-trigger da animacao mesmo com delta igual.
export const lastRankingDelta = writable<{ delta: number; newScore: number; seq: number } | null>(null);
export const avatar = persistentString(STORAGE_KEYS.avatar, 'character1');

// `user` derived a partir das stores individuais — facilita ler o profile
// inteiro num lugar so.
export const user = derived(
  [userId, userName, avatar],
  ([id, name, av]): UserProfile | null => (id ? { id, nickname: name, avatar: av } : null),
);

// `isAuthenticated` muda quando token presente.
export const isAuthenticated = derived(token, ($t) => !!$t);

// Subscribe ao Firebase Auth state. Toda vez que o ID token muda
// (login, logout, renew automatico), atualizamos o store + o userId
// (= Firebase UID). Sem isso, o token cached fica stale apos 1h e
// requests viram 401.
onIdTokenChanged(firebaseAuth, async (fbUser) => {
  if (fbUser) {
    const idToken = await fbUser.getIdToken();
    token.set(idToken);
    userId.set(fbUser.uid);
    // userName + avatar carregados via GET /users/me apos primeiro idToken
    // (orchestrado pelo LoginForm/RegisterForm).
  } else {
    token.set('');
    userId.set('');
    // userName preservado em localStorage pra UI continuar mostrando
    // "Bem-vindo de volta, X" apos logout temporario? Decidir depois.
  }
});

export function persistSession(_t: string, id: string, name: string, avatarKey?: string) {
  // Token e setado automaticamente pelo onIdTokenChanged — argumento
  // ignorado pra manter compat com callers antigos.
  userId.set(id);
  userName.set(name);
  if (avatarKey) avatar.set(avatarKey);
}

export async function clearSession() {
  // Sign out do Firebase — limpa IndexedDB persistence e dispara
  // onIdTokenChanged(null) que zera o token store.
  try {
    await firebaseAuth.signOut();
  } catch (err) {
    console.warn('firebase signOut falhou —', err);
  }
  userId.set('');
  userName.set('');
}

export function getToken(): string {
  return get(token);
}
